import { Box, Stack, styled } from "@mui/material";
import { ChipList, HtmlViewer, Img, Link, LoadingSkeleton, SmText, TextMd } from "Components";
import useGetData from "hooks/useGetData";
import React, { Fragment, lazy, memo, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { apiGetCompanyOverview, apiGetSuppliersByCategory } from "services/api/company";
import SupplierMedias from "./SupplierMedias";
import SupplierAttributes from "./SupplierAttributes";
import SupplierPosts from "./SupplierPosts";
import { CategoryType } from "types";
import { useParams } from "react-router-dom";
import { ExportCountries, SuggestProducts, Verifications } from ".";
import ProductsFromSupplier from "./ProductsFromSupplier";
import { useSupplierContext } from "..";
import { DIRECT_PATHS } from "constants/routes";
import SendInquiryForm from "Components/form/SendInquiryForm";
import Card from "Components/card/Card";
import { withLoading } from "hocs/withLoading";
import { REPORT_TYPE } from "constants/data";
import { Inquiries } from "Components/list";
import useBreakpoints from "hooks/useBreakpoint";
import ContactSupplier from "Components/sections/ContactSupplier";
import { SupplierType } from "types/supplier";
import { showNotification } from "helpers/notification";
import { removeNullFields } from "helpers/form";
import Supplier from "Components/card/Supplier";
import { WarningBladeIcon } from "Assets/icons";
import { theme } from "theme";

const Reviews = withLoading(
  lazy(() => import("../../../Components/list/Reviews"))
);
type SupplierCategoryType = {
  suppliers: SupplierType[],
  href?: string
}
const OverviewTab = () => {
  const t = useIntl();
  const { supplierId } = useParams<{ supplierId: string }>();
  const { isMobile } = useBreakpoints();
  const { supplierDetail, onReloadDetail } = useSupplierContext();
  const [supplierByCategory, setSupplierByCategory] = useState<Record<string, SupplierCategoryType>>({});
  const fetchedCategories = useRef<Set<string>>(new Set())

  const { data, isLoading } = useGetData({
    promise: () => {
      if (supplierId) {
        return apiGetCompanyOverview({ farm_id: supplierId });
      }
    },
    deps: [supplierId],
  });
  const overviewData = useMemo(
    () => data?.data?.farm || {},
    [data?.data?.farm]
  );
  const {
    farm_description,
    farm_media = [],
    attr = [],
    posts = [],
    categories = [],
    export_countries = [],
    enabled,
  } = overviewData;

  const fetchData = async (keyword: string) => {
    if (fetchedCategories.current.has(keyword)) return; // Tránh gọi API trùng lặp
    fetchedCategories.current.add(keyword);

    const pageLimit = {
      page: Number("1"),
      limit: 3,
      // keyword: keyword,
      category_id: keyword,
      sort: "hot",
    };
    try {
      let data = await apiGetSuppliersByCategory(
        removeNullFields({
          ...pageLimit,
          offset: (Number(pageLimit?.page) - 1) * (pageLimit?.limit || 0),
        })
      );
      let responseData = data.data?.message;
      return responseData?.data || [];

    } catch (error) {
      console.log(error);
      showNotification('something_went_wrong')
      return [];
    }
  };
  useEffect(() => {
    if (categories && categories?.length > 0) {
      const threeCategory = categories?.slice(0, 3)
      const fetchAll = async () => {
        await Promise.all(
          threeCategory.map(async (item: any) => {
            // const categoryName = item?.category_name;
            const categoryName = item?.href?.split("/").pop();
            const data = await fetchData(categoryName);
            if (data && data?.length > 0) {
              setSupplierByCategory((prev) => ({
                ...prev,
                [item?.category_name]: {
                  suppliers: data,
                  href: item?.href
                }
              }));
            }
          })
        );
      };

      fetchAll();
    }
  }, [categories]);

  return (
    <StyledOverviewTab spacing={2} mt={2}>
      {isLoading && <LoadingSkeleton />}

      {(attr.length > 0 || farm_description || farm_media.length > 0) && (
        <Card>
          <SupplierAttributes attr={attr} />

          {farm_description && (
            <Fragment>
              <TextMd component="h2">
                {t.formatMessage({ id: "introduction" })}
              </TextMd>
              <HtmlViewer className="description" content={farm_description} />
            </Fragment>
          )}

          <SupplierMedias medias={farm_media} />
        </Card>
      )}

      {isMobile && (
        <ContactSupplier className="card" supplierId={supplierDetail?.name} />
      )}

      {/* Following categories */}
      {categories.length > 0 && (
        <Card>
          <TextMd>{t.formatMessage({ id: "supplier_categories" })}</TextMd>
          <ChipList
            chips={categories?.map(({ category_name, href }: CategoryType) => ({
              label: category_name,
              value: category_name,
              slug: href,
            }))}
            onDirectChip={DIRECT_PATHS.categorySupplier}
            defaultCollapsed={false}
            {...{ isLoading }}
          />
        </Card>
      )}

      {/* Supplier's products */}
      <ProductsFromSupplier {...{ supplierId }} className="card" />

      {/* Verify section */}
      <Verifications {...{ supplierDetail }} />

      {/* Export countries */}
      <ExportCountries exportCountries={export_countries} />

      <SupplierPosts {...{ posts }} />

      {/* User reviews */}
      <Reviews
        type={REPORT_TYPE.supplier}
        title={supplierDetail.farm_name}
        reference_name={supplierId}
        onComplete={onReloadDetail}
      />

      {/* Sent inquiries */}
      <Inquiries farm_id={supplierId} />
      <Card>
        {false &&
          <StyleBlur>
            <TextMd>{t.formatMessage({ id: "send_direct_inquiry" })}</TextMd>
            <Img
              src="/images/background/blur_quotation.png"
              alt="background"
              width="100%"
              style={{ objectFit: "cover", borderRadius: "8px" }}
            />
            <Box className="content" id="inquiry-form">
              <WarningBladeIcon />
              <TextMd fontSize={isMobile ? 14 : 16}>{t.formatMessage({ id: "verification_in_progress" })}</TextMd>
              <SmText fontSize={isMobile ? 12 : 14}>{t.formatMessage({ id: "info_reference_only" })}</SmText>
              <SmText fontSize={isMobile ? 12 : 14}>{t.formatMessage({ id: "recommend_verified_suppliers" })}</SmText>
            </Box>
          </StyleBlur>}
        {1 && <SendInquiryForm supplierData={supplierDetail} />}
      </Card>
      {isMobile && <SuggestProducts className="card" />}
      {supplierByCategory && (
        <Fragment>
          {Object.entries(supplierByCategory).map(([categories, value]) => (
            <Box className="card">
              <TextMd marginBottom={'12px'}>{t.formatMessage({ id: "more_suppliers_similar_to" })}: <Link style={{ color: "#008934" }} to={value?.href || "#"}>{categories}</Link></TextMd>
              {value?.suppliers.map((supplier: SupplierType, i: number) => (
                <div key={i} style={{ marginTop: '12px' }}>
                  <Supplier company={supplier} isPageDetail />
                </div>
              ))}
            </Box>
          ))}
        </Fragment>
      )}
    </StyledOverviewTab>
  );
};

const StyledOverviewTab = styled(Stack)({
  ".description": {
    color: "var(--gray-color)",
    fontSize: 14,
  },
});
const StyleBlur = styled(Stack)({
  position: "relative",
  background: "var(--white-color)",
  borderRadius: "8px",
  '.content': {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    textAlign: "center",
    width: "100%",
    height: "100%",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    height: "200px",
    '.content': {
      width: "100%",
      height: "100%",
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      textAlign: "center",
      '>svg': {
        width: "32px",
        height: "32px",
      },
    },
  }
})
export default memo(OverviewTab);
